import { library } from '@fortawesome/fontawesome-svg-core'
import { fab } from '@fortawesome/free-brands-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import * as Sentry from '@sentry/vue'
import { VueQueryPlugin } from '@tanstack/vue-query'

import './style.css'
import { createApp } from 'vue'

import App from './App.vue'
import router from './router'

const app = createApp(App)

// Setup FontAwesome
library.add(fab)
app.component('FA', FontAwesomeIcon)

// Initialize Sentry
if (import.meta.env.PROD && ['psiderman.com'].includes(window.location.hostname)) {
  Sentry.init({
    app,
    // Only send errors in production
    beforeSend(event) {
      if (import.meta.env.DEV) {
        return null
      }
      return event
    },
    dsn: import.meta.env.VITE_SENTRY_DSN as string,
    // Logs
    enableLogs: true,
    // Environment
    environment: import.meta.env.MODE,
    ignoreErrors: [
      "Unexpected token '<'",
      'loading chunk',
      'Failed to fetch dynamically imported module',
      'Unable to preload CSS',
      'AbortError: Lock broken',
      /ResizeObserver loop/,
      /Java object is gone/,
    ],
    integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/psiderman\.com\/api/],
    // Tracing: 25% sample — enough signal for a personal site without full quota burn
    tracesSampleRate: 0.25,
  })
}

import lazy from './directives/lazy'
import reveal from './directives/reveal'
import tooltip from './directives/tooltip'
import { queryClient } from './queryClient'

app.use(router)
app.directive('tooltip', tooltip)
app.directive('lazy', lazy)
app.directive('reveal', reveal)
app.use(VueQueryPlugin, { queryClient })

// Let vue-router own scroll restoration so the browser never races the router's
// scroll reset during client-side navigations.
if ('scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual'
}

// Auto-recover from stale chunks deployed after the current tab was opened
function onPreloadError(event: Event) {
  event.preventDefault()
  window.removeEventListener('vite:preloadError', onPreloadError)
  const key = 'vite_preload_reload'
  if (!sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, '1')
    window.location.reload()
  }
}

window.addEventListener('vite:preloadError', onPreloadError, { once: true })

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    window.removeEventListener('vite:preloadError', onPreloadError)
  })
}

await router.isReady()
app.mount('#app')
